import { SelectMarketType } from '../../features';
import { BetType, OddDTO, SportType, TeamLeagueType } from '../odds';
import { DeepOmit } from 'deep-utility-types';

export interface ParlayBetData {
  id: string;
  title: string;
  subTitle: string;
  betAmount: string;
  betInfo: string;
  betDetails: string;
  homeTeamName: string;
  awayTeamName: string;
}

export interface ResponseBetsData {
  PendingBets: Bet[];
  ApprovedBets: Bet[];
  Forecast: Forecast;
}
export interface SingleBetDTO {
  id?: string;
  SocTransID: number;
  ReferenceNo: string;
  MinBet: number;
  MaxBet: number;
  AvailableBetAmount: number;
  DangerStatus: string;
  OddsChanged: boolean;
  OddsValue: number;
  IsHomeGive: boolean;
  HomeScore: number;
  AwayScore: number;
  SpecialValue: number;
  AOSCores: number;
}
export enum ResultCode {
  SUCCESS_NORMAL = 0,
  SUCCESS_DANGER = 1,
  FAILED = -1,
  MATCH_HIDE = -3,
  MATCH_PAUSED = -5,
  ODDS_CHANGED = -9,
  SCORE_CHANGED = -17,
  BET_FAILED = -20,
  INSUFFICIENT_FUNDS = -10,
}
export interface SingleBetResponse {
  ResultCode: ResultCode;
  ResultRemark: string;
  ResultDetail: SingleBetDTO;
}
export interface ParlayBetDTO {
  Code: number;
  Data: any;
}
export interface ParlayBetResponse {
  ResultCode: ResultCode;
  ResultRemark: string;
  ResultDetail: ParlayBetResultDetail;
}
interface ParlayBetResultDetail {
  MinBet: number;
  MaxBet: number;
  AvailableBetAmount: number;
  OddsChanged: boolean;
  OddsChangedDetail: OddsChangedDetail[];
  ParlayTransDetail: ParlayTransDetail[];
}

interface OddsChangedDetail {
  MatchGroupID: string;
  MinBet: number;
  MaxBet: number;
  OddsChanged: boolean;
  OddsValue: number;
  IsHomeGive: boolean;
  HomeScore: number;
  AwayScore: number;
  SpecialValue: number;
  AOSCores: number;
}

interface ParlayTransDetail {
  SocTransID: number;
  ParlayBetItems: ParlayBetItem[];
}

interface ParlayBetItem {
  SocTransID: number;
  ReferenceNo: string;
  DangerStatus: string;
  MatchGroupID: string;
  SocTransParID: string;
  ParDangerStatus: string;
}

export interface Bet {
  ReferenceNo: string;
  SportType: string;
  SportName: string;
  TransType: string;
  TransName: string;
  Amount: number;
  MatchDate: string;
  LeagueName: string;
  Home: string;
  Away: string;
  IsRunning: boolean;
  IsHomeGive: boolean;
  Bet: string;
  Odds: number;
  HDP?: number;
  OU?: number;
  RunHomeScore: number;
  RunAwayScore: number;
  DangerStatus: string;
}

export interface ParlayInfoData {
  id: string;
  title: string;
  betAmount: string;
  betInfo: string;
}

export interface ParlayBetData {
  id: string;
  title: string;
  subTitle: string;
  betAmount: string;
  betInfo: string;
  betDetails: string;
  betName?: string;
  homeTeamName: string;
  awayTeamName: string;
}

export interface ResponseBetsData {
  PendingBets: Bet[];
  ApprovedBets: Bet[];
  Forecast: Forecast;
}
export default interface ResponseBets {
  Success: boolean;
  Code: number;
  Message: string;
  Data: ResponseBetsData;
}

export interface SingleBetRequest {
  BetterOdds: boolean;
  BetArgs: BetArgs;
}
export type SingleBetRequestWithoutOddsType = DeepOmit<SingleBetRequest, 'BetArgs.OddsType'>;

export interface ParlayBetRequest {
  BetterOdds: boolean;
  CombineFlag: number;
  BetArgs: BetArgs[];
  AOSCores: number;
  Match?: OddDTO;
}
export interface SingleBetPayload extends SingleBetRequest {
  Match: OddDTO;
  _changed?: {
    [key in ChangeableKeys]?: [BetArgs[key], BetArgs[key]];
  };
  _paused?: boolean;
  _insufficientFunds?: boolean;
}
export interface ParlayBetPayload extends ParlayBetRequest {
  id: string;
  Match: OddDTO;
}
export enum OddsType {
  MY = 'MY',
  HK = 'HK',
  DE = 'DE',
  ID = 'ID',
  US = 'US',
}

export type ChangeableKeys =
  | 'OddsValue'
  | 'OddsType'
  | 'SpecialValue'
  | 'AOSCores'
  | 'BetAmt'
  | 'HomeScore'
  | 'AwayScore';
export type BetPositionLabelType =
  | string
  | [['TeamLeagueName', TeamLeagueType, number, number]]
  | [['TeamLeagueName', TeamLeagueType, number, number], string];
export interface BetArgs {
  id?: string;
  BetType: BetType;
  MatchID: number;
  IsFH: boolean;
  OddsValue: number;
  BetPosition: string;
  IsHomeGive: boolean;
  HomeScore: number;
  AwayScore: number;
  BetAmt: number;
  OddsType: OddsType;
  SpecialValue: number;
  AOSCores: number;
  BetPositionLabel?: BetPositionLabelType;
  OddsChanged?: boolean;
  /**
   * internally use for HDP or OU for example
   */
  _indexed?: number;
}

export enum CurrencyType {
  CNY = 'CNY',
  IDR = 'IDR',
  KRW = 'KRW',
  MMK = 'MMK',
  MYR = 'MYR',
  SGD = 'SGD',
  THB = 'THB',
  USD = 'USD',
  VD = 'VND',
  BND = 'BND',
  INR = 'INR',
  PHP = 'PHP',
  KHUSD = 'KHUSD',
  JPY = 'JPY',
  SGUSD = 'SGUSD',
  SGEUR = 'SGEUR',
  EUR = 'EUR',
  USEUR = 'USEUR',
  KHEUR = 'KHEUR',
  CNUSD = 'CNUSD',
  CNEUR = 'CNEUR',
  INUSD = 'INUSD',
  INEUR = 'INEUR',
  IDUSD = 'IDUSD',
  IDEUR = 'IDEUR',
  KRUSD = 'KRUSD',
  KREUR = 'KREUR',
  MYUSD = 'MYUSD',
  MYEUR = 'MYEUR',
  VNUSD = 'VNUSD',
  VNEUR = 'VNEUR',
  JPUSD = 'JPUSD',
  THEUR = 'THEUR',
  THUSD = 'THUSD',
  AUD = 'AUD',
  USDT = 'USDT',
  BRL = 'BRL',
}

export interface BetRecordDTO {
  Id: number;
  UserName: string;
  RefNo: string;
  SocTransId: number;
  SocOddsListID: string;
  MatchId: number;
  Is1H: boolean;
  IsHomeGive: boolean;
  IsBetHome: boolean;
  TransType: string;
  ActualAmt: number;
  Hdp: number;
  DisplayHdp: string;
  Odds: number;
  OddType: OddsType;
  Currency: CurrencyType;
  ExRate: number;
  BetMinute: number;
  DangerStatus: 'D' | 'N' | 'A' | 'C' | 'R';
  RiskLose: number;
  SoldTicketMoney: number;
  RunHomeScore: number;
  RunAwayScore: number;
  IsRun: boolean;
  GameType: SportType;
  Choice: string;
  TransDate: string;
  Home: string;
  Away: string;
  LeagueId: number;
  LeagueCode: string;
  SellValue: number;
  IsTradeInAllowed: boolean;
  TradeInRiskWinSum: number;
  TradeLastStatus: string;
  TradeLastTradeInRiskWin: number;
  StatusChangeTradeIn: number;
  TradeInPriceSum: number;
  MatchIsTradeIn: boolean;
  Percent: number;
  Ex: string;
  MatchDate: string;
  IsSold: boolean;
  MeisTradeIn: boolean;
  BetAmount?: number;
  WinAmount?: number;
  ExchangeRate?: number;
  ReferenceNo?: string;
  ParlayBetDetails?: StatementParlayRecordDTO[];
}
export interface Forecast {
  Live: number;
  Today: number;
  Early: number;
}

export interface MiniBetListWrapperDTO {
  PendingBets: BetRecordDTO[];
  ApprovedBets: BetRecordDTO[];
  Forecast: Forecast;
}

export enum BettingCartType {
  Parlay = 'Parlay',
  Single = 'Single',
}

export enum MatchStatus {
  Pending = 'P',
  Completed = 'C',
  RefundFT = 'R',
  Unknown = 'U',
}

export interface ResultsDTO {
  leagueId: string;
  leagueName?: string;
  oddList: ResultDTO[];
}

export interface ResultDTO {
  MatchID: number;
  SportType: SportType;
  MatchGroupID: number;
  LeagueID: number;
  SpecialID: number;
  HomeTeamID: number;
  AwayTeamID: number;
  MatchDate: string;
  MatchStatus: MatchStatus;
  Nuetral: boolean;
  HomeScore_FH: number;
  AwayScore_FH: number;
  HomeScore_FT: number;
  AwayScore_FT: number;
  MatchOver_FH: boolean;
  MatchOver_FT: boolean;
}

export interface ResultFilter {
  MatchDate?: string;
  SportType?: SportType;
  LeagueID?: string;
  MarketFilter?: SelectMarketType;
  StartDate?: string;
  EndDate?: string;
  Refresh: boolean;
  Type: 'R' | 'O';
  sort: string;
}

export interface ResultFilterProps {
  onChange: (value: any) => void;
  filter: ResultFilter;
}

export interface StatementFilter {
  Week: 'lastWeek' | 'thisWeek';
  TimeZone: string;
  Refresh: boolean;
}

export interface StatementDetailFilter {
  CurrentDate: number;
  TimeZone: string;
  Refresh: boolean;
}

export interface StatementDTO {
  BetRemarks: string;
  Currency: string;
  Date: number;
  TurnOver: number;
  WinAmount: number;
}

export interface StatementFilterProps {
  onChange: (value: any) => void;
  filter: StatementFilter;
}

export interface StatementRecordDTO {
  Id: number;
  BetId: number;
  LoginName: string;
  ReferenceNo: string;
  SocTransId: number;
  IsFirstHalf: boolean;
  TransDate: number;
  IsHomeGive: boolean;
  IsBetHome: boolean;
  BetAmount: number;
  Outstanding: number;
  Hdp: number;
  Odds: number;
  Currency: string;
  WinAmount: number;
  ExchangeRate: number;
  WinLoseStatus: string;
  TransType: BetType;
  DangerStatus: 'D' | 'N' | 'A' | 'C' | 'R';
  MemCommission: number;
  BetIp: string;
  HomeScore: number;
  AwayScore: number;
  RunHomeScore: number;
  RunAwayScore: number;
  IsRunning: boolean;
  RejectReason: '';
  SportType: SportType;
  Choice: number;
  WorkingDate: string;
  OddsType: string;
  MatchDate: number;
  HomeTeamId: number;
  AwayTeamId: number;
  LeagueId: number;
  SpecialId: string;
  StatusChange: number;
  StateUpdateTs: number;
  MemCommissionSet: number;
  IsCashOut: false;
  CashOutTotal: number;
  CashOutTakeBack: number;
  CashOutWinLoseAmount: number;
  BetSource: number;
  AosExcluding: string;
  MmrPercent: number;
  MatchID: number;
  MatchGroupID: string;
  BetRemarks: string;
  IsSpecial: boolean;
  MerchantId: number;
  ParlayBetRecords?: StatementParlayRecordDTO[];
}

export interface StatementParlayRecordDTO {
  Id: number;
  LoginName: string;
  Amt: number;
  AwayId: number;
  AwayScore: number;
  Choice: number;
  CreatedTsDate: number;
  Currency: string;
  DangerStatus: 'D' | 'N' | 'A' | 'C' | 'R';
  ExRate: number;
  FtScore: string;
  Hag: number;
  Hdp: number;
  HomeId: number;
  HomeScore: number;
  HtScore: string;
  IsBetHome: true;
  IsFH: false;
  IsHomeGive: true;
  IsRun: false;
  LeagueId: number;
  MatchGroupId: string;
  MatchId: number;
  Odds: number;
  ParDangerStatus: 'D' | 'N' | 'A' | 'C' | 'R';
  ParOdds: number;
  ParStatus: string;
  ParTransType: string;
  RefNo: string;
  RiskLose: number;
  RiskWin: number;
  SocTransId: number;
  SocTransParId: number;
  SpecialId: string;
  SportType: SportType;
  WinLoseStatus: string;
  TransDate: number;
  TransType: BetType;
  UpdatedTsDate: number;
  WinAmt: number;
  WinRate: number;
  StateUpdateTs: number;
  AosExcluding: number;
  CmdBetRecordId: number;
}

export interface OutrightsDTO {
  SportType: SportType;
  LeagueID: number;
  SpecialID: number;
  HomeTeamID: number;
  AwayTeamID: number;
  MatchDate: string;
  MatchStatus: MatchStatus;
  Nuetral: boolean;
  HomeScore_FT: number;
  MatchOver_FH: boolean;
  MatchOver_FT: boolean;
}
